import {
  Component,
  ChangeDetectorRef,
  DestroyRef,
  effect,
  inject,
  input,
  output
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateDirective } from '@ngx-translate/core';
import {
  BlockDirective,
  ButtonComponent,
  CardComponent,
  CardContentComponent,
  CardFooterComponent,
  CardMediaDirective,
  CardPrimaryActionComponent,
  CardSubtitleComponent,
  CardTitleComponent,
  DataAnalyticsTaggingDirective,
  DataAnalyticsTaggingConstants,
  DataAnalyticsTaggingEvent,
  DatePipe,
  IconComponent,
  IdService,
  ImagePlaceholderComponent,
  TranslatePipe,
  ToastMessageService
} from '@tdwb/core';
// eslint-disable-next-line td-wb-rules/cross-lazyload-module-import-rule
import { LearnDefaultImageDirective } from '@tdwb/lazy/learn';
import { LearnRestService, Lesson, RouterService } from '@tdwb/webbroker/core';
import { LearnDurationComponent } from '../duration/learn-duration.component';
@Component({
  selector: 'td-wb-learn-saved-video',
  templateUrl: './learn-saved-video.component.html',
  styleUrls: ['./learn-saved-video.component.scss'],
  host: {
    '[style.width.px]': 'width()'
  },
  imports: [
    DataAnalyticsTaggingDirective,
    CardComponent,
    ImagePlaceholderComponent,
    CardMediaDirective,
    LearnDefaultImageDirective,
    LearnDurationComponent,
    CardContentComponent,
    CardSubtitleComponent,
    CardTitleComponent,
    CardFooterComponent,
    CardPrimaryActionComponent,
    IconComponent,
    DatePipe,
    ButtonComponent,
    BlockDirective,
    TranslatePipe,
    TranslateDirective
  ]
})
export class LearnSavedVideoComponent {
  private readonly learnRestService = inject(LearnRestService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly routerService = inject(RouterService);
  private readonly idService = inject(IdService);
  private readonly destroyRef = inject(DestroyRef);
  readonly lesson = input.required<Lesson>();
  readonly sourceName = input<string>();
  readonly width = input<number>();
  readonly bookmarkChange = output<boolean>();
  readonly lessonSelected = output<Lesson>();
  uniqueId: string;
  bookmarkUniqueId: string;
  saveDisabled: boolean = false;
  readonly aspectRatio: string = '16:9';
  eventData: DataAnalyticsTaggingEvent = {};
  constructor() {
    effect(() => {
      this.generateUniqueId(this.sourceName());
    });
  }

  toggleSave(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const lesson = this.lesson();
    this.saveDisabled = true;
    this.learnRestService.toggleVideoBookMark(lesson)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (): void => {
          lesson.saved = !lesson.saved;
          this.bookmarkChange.emit(lesson.saved);
          this.saveDisabled = false;
          const messageKey = lesson.saved ? 'tdWb.learn.notification.SAVED' : 'tdWb.learn.notification.UNSAVED';
          const action = lesson.saved ? 'saved' : 'unsaved';
          this.eventData = DataAnalyticsTaggingConstants.getLinkClickEventData(`tdw:wbt:learn:${this.getVideoSection()}:${action}:${lesson.title}`);
          this.toastMessageService.setToast(messageKey);
          this.changeDetectorRef.markForCheck();
        },
        error: (): void => {
          this.saveDisabled = false;
        }
      });
  }

  getVideoSection(): string {
    let videoSection;
    switch(this.sourceName()) {
      case 'recommendation':
        videoSection = 'Previously Watched';
        break;
      case 'most-liked':
        videoSection = 'Most Popular';
        break;
      case 'new-account':
        videoSection = 'New Here';
        break;
      case 'selected-topics':
        videoSection = 'Selected Topics';
        break;
    }
    return videoSection;
  }

  selectLesson(): void {
    const lesson = this.lesson();
    const videoSection = this.getVideoSection();
    this.eventData = DataAnalyticsTaggingConstants.getVideoPageEventData('lesson', videoSection, lesson.title);
    if (this.sourceName() === 'related-lessons') {
      this.lessonSelected.emit(lesson);
    } else {
      this.routerService.go('page.learn.lesson', { id: lesson.uniqueKey, section: videoSection });
    }
  }

  private generateUniqueId(sourceName: string | undefined): void {
    const prefix = sourceName ?
      'td-wb-learn-saved-video-lc-' + sourceName + '-card' :
      'td-wb-learn-saved-video-lc-card';
    this.uniqueId = this.idService.generateNamespaceSequentialId(prefix);
    const bookmarkPrefix = sourceName ? 'td-wb-learn-saved-video-lc-' + sourceName + '-bookmark' :
      'td-wb-learn-saved-video-save';
    this.bookmarkUniqueId = this.idService.generateNamespaceSequentialId(bookmarkPrefix);
  }
}
